import styles from './Button.module.scss';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { Button as BaseButton, VisuallyHidden } from '@ariakit/react';

const Button = forwardRef(
  (
    {
      as = null,
      children,
      className = null,
      disabled = false,
      isAnimated = false,
      isElevated = false,
      isResponsive = false,
      isLoading = false,
      render = null,
      shape = null,
      size = 'default',
      theme = 'primary',
      variant = null,
      ...props
    },
    ref,
  ) => {
    // Workaround for the deprecated `as` prop: https://github.com/ariakit/ariakit/releases/tag/@ariakit/react@0.3.0
    if (as) {
      const error = new Error(
        'Wien Energie Design System: The `as` property in the Button component is deprecated. Please use the `render`property instead.',
      );

      // eslint-disable-next-line no-console
      console.warn(error.message, error.stack);

      const Tag = as;

      render = <Tag />;
    }

    return (
      <BaseButton
        className={classNames(
          styles[theme],
          styles[size],
          styles.container,
          {
            [styles[shape]]: shape,
            [styles[variant]]: variant,
            [styles.elevated]: isElevated,
            [styles.responsive]: isResponsive,
            [styles.disabled]: disabled,
            [styles.animated]: isAnimated,
          },
          className,
        )}
        disabled={isLoading || disabled}
        ref={ref}
        render={render || undefined}
        {...props}
      >
        <>
          {isLoading && (
            <div className={styles.isLoading}>
              <VisuallyHidden>Loading...</VisuallyHidden>
            </div>
          )}
          {isAnimated && (
            <span className={styles.buttonAnimatedContent}> {children}</span>
          )}
          {!isAnimated && !isLoading && children}
          {isAnimated && (
            <div className={styles.animatedEffectContainer}>
              <span className={classNames(styles.effect, styles[theme])} />
              <span className={classNames(styles.effect2, styles[theme])} />
              <span className={classNames(styles.effect3, styles[theme])} />
            </div>
          )}
        </>
      </BaseButton>
    );
  },
);

Button.propTypes = {
  /** **DEPRECATED** - Use `render` instead. */
  as: PropTypes.elementType,
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  isAnimated: PropTypes.bool,
  /** Elevated from background element */
  isElevated: PropTypes.bool,
  isLoading: PropTypes.bool,
  isResponsive: PropTypes.bool,
  /** A React element which will render the root component. Use this instead of `as` */
  render: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  shape: PropTypes.oneOf(['bubble', 'squared']),
  size: PropTypes.oneOf(['default', 'small', 'large']),
  theme: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'ghost',
    'contrast',
  ]),
  variant: PropTypes.oneOf(['link', 'outline', 'bubble']),
};

Button.displayName = 'Button';

export default Button;
